import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, {
  CheckUserExpiry,
  isIndependentScientistUser,
} from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { formatDisplayDate } from "../../utils/formatDisplayDate";

const cardRadius = { borderRadius: "14px" };

const ScientistInvites = () => {
  const [invites, setInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [respondingId, setRespondingId] = useState(null);

  const loadInvites = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("api/User/MyScientistInvites");
      setInvites(Array.isArray(data) ? data : []);
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to load invitations");
    }
  }, []);

  useEffect(() => {
    if (!isIndependentScientistUser()) {
      setIsLoading(false);
      return;
    }
    (async () => {
      await CheckUserExpiry();
      await loadInvites();
      setIsLoading(false);
    })();
  }, [loadInvites]);

  const respond = async (inviteId, accept) => {
    setRespondingId(inviteId);
    try {
      await axiosInstance.post("api/User/RespondScientistInvite", { inviteId, accept });
      showSuccessToast(accept ? "Invitation accepted" : "Invitation declined");
      await loadInvites();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to update invitation");
    } finally {
      setRespondingId(null);
    }
  };

  if (isLoading) return <Preloader />;

  if (!isIndependentScientistUser()) {
    return (
      <div className="donezo-page container-fluid px-3 py-5">
        <div className="mx-auto bg-white shadow-sm p-4 text-center" style={{ ...cardRadius, maxWidth: 440 }}>
          <h2 className="h5">Independent scientist account required</h2>
          <Link to="/pond" className="btn btn-primary rounded-pill mt-3">
            Go to ponds
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="donezo-page container-fluid px-3 px-md-4 py-3 py-md-4">
      <h1 className="h3 fw-semibold mb-2">Invitations</h1>
      <p className="text-muted small mb-4">
        Accept an invitation to join a pond owner&apos;s team. Pond access is assigned by the owner after you accept.
      </p>

      {invites.length === 0 ? (
        <div className="card border-0 shadow-sm p-4 text-center" style={cardRadius}>
          <p className="text-muted mb-2">No pending invitations.</p>
          <p className="small text-muted mb-0">
            When a pond owner invites you by email, requests will appear here.
          </p>
          <Link
            to="/pond"
            className="btn btn-outline-primary rounded-pill mt-3 d-block mx-auto"
            style={{ width: "50%" }}
          >
            View assigned ponds
          </Link>
        </div>
      ) : (
        <div className="row g-3">
          {invites.map((inv) => (
            <div className="col-md-6 col-lg-4" key={inv.id}>
              <div className="card border-0 shadow-sm h-100" style={cardRadius}>
                <div className="card-body p-4 d-flex flex-column">
                  <h2 className="h6 fw-semibold">{inv.Fisher?.FullName || "Fisher organization"}</h2>
                  <p className="small text-muted mb-2">
                    Invited {inv.createdAt ? formatDisplayDate(inv.createdAt) : ""}
                  </p>
                  {inv.Message && (
                    <p className="small border-start ps-2 mb-3 text-secondary">{inv.Message}</p>
                  )}
                  <div className="mt-auto d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-success btn-sm rounded-pill px-3"
                      disabled={respondingId === inv.id}
                      onClick={() => respond(inv.id, true)}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                      disabled={respondingId === inv.id}
                      onClick={() => respond(inv.id, false)}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScientistInvites;
